import { useCallback, useEffect, useState } from 'react';
import {
  getDeclarations,
  getDeclarationPreviewUrl,
  uploadSignedDeclaration,
} from '../services/taxFilingsService';
import type { TaxFilingDocument } from '../types/taxFilings.types';
import { useTaxYearStore } from '@/features/tax-year/store/taxYearStore';
import { handleApiError } from '@/lib/utils/errorHandler';
import { toast } from 'sonner';
import {
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE,
} from '@/features/files/types/file.types';

export function useDeclarations() {
  const [documents, setDocuments] = useState<TaxFilingDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const effectiveYear = useTaxYearStore((s) => s.getEffectiveYear());
  const configFetched = useTaxYearStore((s) => s.configFetched);

  const fetchDocuments = useCallback(async () => {
    if (effectiveYear == null) return;
    setIsLoading(true);
    setError(null);
    try {
      const list = await getDeclarations(effectiveYear);
      setDocuments(list);
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  }, [effectiveYear]);

  useEffect(() => {
    if (!configFetched || effectiveYear == null) return;
    fetchDocuments();
  }, [configFetched, effectiveYear, fetchDocuments]);

  const getPreviewUrl = useCallback(async (documentId: string): Promise<string | null> => {
    try {
      return await getDeclarationPreviewUrl(documentId);
    } catch (err) {
      handleApiError(err);
      toast.error('Could not open preview');
      return null;
    }
  }, []);

  const uploadSigned = useCallback(
    async (files: File[]): Promise<boolean> => {
      if (effectiveYear == null) {
        toast.error('Tax year is not ready yet. Please try again.');
        return false;
      }

      if (files.length === 0) return false;

      const allowedTypes = Object.keys(ALLOWED_FILE_TYPES);
      for (const file of files) {
        if (file.size > MAX_FILE_SIZE) {
          toast.error(`File "${file.name}" exceeds maximum size of 10MB`);
          return false;
        }
        if (!allowedTypes.includes(file.type)) {
          toast.error(`File type "${file.type}" is not allowed`);
          return false;
        }
      }

      setIsUploading(true);
      try {
        for (const file of files) {
          await uploadSignedDeclaration(file, effectiveYear);
        }
        toast.success(
          files.length === 1
            ? `"${files[0].name}" uploaded successfully`
            : `${files.length} signed declarations uploaded successfully`
        );
        await fetchDocuments();
        return true;
      } catch (err) {
        handleApiError(err);
        toast.error('Failed to upload signed declaration');
        return false;
      } finally {
        setIsUploading(false);
      }
    },
    [effectiveYear, fetchDocuments]
  );

  return {
    documents,
    isLoading: isLoading || !configFetched,
    error,
    isUploading,
    refresh: fetchDocuments,
    getPreviewUrl,
    uploadSigned,
  };
}
